import type { CreatedUpload, UploadInfo } from '@hemmafixa/shared';
import { formatFileSize, type FileGalleryItem } from '@hemmafixa/ui';
import { useCallback, useState } from 'react';
import { fill, useI18n } from '@/i18n/context';
import { ApiError } from '@/lib/api';

/**
 * PUTs a file straight to storage with the signed link the API handed out. Not through the API client: the link
 * points at the bucket, carries its own signature and must not get our cookies.
 */
export async function putToStorage(created: CreatedUpload, file: File): Promise<void> {
  const response = await fetch(created.uploadUrl, {
    method: 'PUT',
    headers: created.uploadHeaders,
    body: file,
  });
  if (!response.ok) throw new Error(`Storage answered ${response.status}`);
}

export const isImage = (contentType: string) => contentType.startsWith('image/');

/** A saved file as the gallery shows it */
export function galleryItem(upload: UploadInfo): FileGalleryItem {
  return {
    id: upload.id,
    name: upload.fileName,
    kind: isImage(upload.contentType) ? 'image' : 'document',
    status: upload.status === 'rejected' ? 'failed' : upload.status === 'ready' ? 'ready' : 'uploading',
    url: upload.url,
    thumbnailUrl: upload.thumbnailUrl,
    size: formatFileSize(upload.sizeBytes),
  };
}

interface UploadSteps {
  /** Asks the API for a link (and creates the row) */
  create: (file: File) => Promise<CreatedUpload>;
  /** Tells the API the file is there, so it checks it */
  complete: (uploadId: string) => Promise<UploadInfo>;
  /** Called after each file finished or failed, e.g. to reload the list */
  onSettled: () => void;
  /** A readable reason for a failure */
  errorMessage: (error: unknown) => string;
}

/**
 * Uploads files one after the other (link → PUT → check) and keeps a placeholder for each while it runs, so the
 * gallery shows progress. Failed ones stay with their reason until removed or the list reloads.
 */
export function useFileUploads({ create, complete, onSettled, errorMessage }: UploadSteps) {
  const [pending, setPending] = useState<FileGalleryItem[]>([]);

  const upload = useCallback(
    async (files: File[]) => {
      const items = files.map((file, index) => ({
        file,
        item: {
          id: `local-${Date.now()}-${index}`,
          name: file.name,
          kind: isImage(file.type) ? ('image' as const) : ('document' as const),
          status: 'uploading' as const,
          size: formatFileSize(file.size),
        },
      }));
      setPending((current) => [...current, ...items.map((entry) => entry.item)]);
      for (const { file, item } of items) {
        try {
          const created = await create(file);
          await putToStorage(created, file);
          await complete(created.upload.id);
          setPending((current) => current.filter((other) => other.id !== item.id));
        } catch (error) {
          setPending((current) =>
            current.map((other) =>
              other.id === item.id ? { ...other, status: 'failed', error: errorMessage(error) } : other,
            ),
          );
        }
        onSettled();
      }
    },
    [create, complete, onSettled, errorMessage],
  );

  const dismiss = useCallback(
    (id: string) => setPending((current) => current.filter((item) => item.id !== id)),
    [],
  );
  return { pending, upload, dismiss };
}

/** The API's own message for refused files (wrong type, too large); a general one otherwise. */
export function uploadErrorMessage(error: unknown, fallback: string, wrongFile: string) {
  if (error instanceof ApiError && error.code === 'VALIDATION_FAILED') return wrongFile;
  return fallback;
}

/** The translated texts FileUpload and FileGallery need */
export function useFileTexts() {
  const { t } = useI18n();
  const f = t.files;
  return {
    gallery: {
      remove: (name: string) => fill(f.remove, { name }),
      uploading: f.uploading,
      failed: f.failed,
      preparing: f.preparing,
    },
    upload: {
      choose: f.choose,
      remove: (name: string) => fill(f.remove, { name }),
      typeNotAllowed: (name: string) => fill(f.typeNotAllowed, { name }),
      tooLarge: (name: string, limit: string) => fill(f.tooLarge, { name, limit }),
      tooMany: (max: number) => fill(f.tooMany, { max }),
    },
  };
}
